import { Dropdown, Icon, Image, Menu } from "semantic-ui-react";
import React, { useContext } from "react";

import { AuthContext } from "./AuthContextProvider";
import { Link } from "react-router-dom";
import QuickSearch from "./QuickSearch";
import { Role } from "../types";
import config from "../config";
import { lexServLogo } from "../services/Constants";
import { useAuth0 } from "@auth0/auth0-react";
import { viableUserRoles } from "./CatchAllRedirect";

const connectUrl = config.connectWebUrl;
const trustUrl = config.trustWebUrl;
const uwUrl = config.uwWebUrl;

const Navbar: React.FC = () => {
    const { logout, isAuthenticated, user } = useAuth0();
    const { userHasRole, userHasAnyRole } = useContext(AuthContext);

    if (!isAuthenticated || !userHasAnyRole(viableUserRoles)) {
        return <></>;
    }

    return (
        <Menu fluid style={{ position: "relative", zIndex: 200 }}>
            {/* zIndex required to supersede HandsOnTable header */}
            <Menu.Menu position="left">
                <Menu.Item content={<Image src={lexServLogo} style={{ height: "25px", width: "100px" }} />} />
                <Dropdown item text="Search" style={{ borderLeft: "1px solid #22242626" }}>
                    <Dropdown.Menu className="navbar-menu-item">
                        {userHasRole(Role.ServicingUser) && (
                            <>
                                <div className="navbar-dropdown-group-title">Servicing</div>
                                <Dropdown.Item as={Link} to="/carrier" content="Carrier" />
                                <Dropdown.Item as={Link} to="/client" content="Client" />
                                <Dropdown.Item as={Link} to="/contact" content="Contact" />
                                <Dropdown.Item as={Link} to="/insured" content="Insured" />
                                <Dropdown.Item as={Link} to="/policy" content="Policy" />
                                <Dropdown.Item as={Link} to="/portfolio" content="Portfolio" />
                            </>
                        )}

                        {userHasRole(Role.ServicingTrading) && (
                            <>
                                <div className="navbar-dropdown-divider" />
                                <div className="navbar-dropdown-group-title">Trading</div>
                                <Dropdown.Item as={Link} to="/trading-company" content="Company" />
                            </>
                        )}

                        {userHasRole(Role.TrustUser) && (
                            <>
                                <div className="navbar-dropdown-divider" />
                                <div className="navbar-dropdown-group-title">Trust</div>
                                <Dropdown.Item as="a" href={`${trustUrl}/carrier`} content="Carrier" />
                                <Dropdown.Item as="a" href={`${connectUrl}/TrustManagement/Client.aspx?IiqClient=1`} content="Client" />
                                <Dropdown.Item as="a" href={`${trustUrl}/client`} content="Client Asset Allocation" />
                                <Dropdown.Item as="a" href={`${trustUrl}/contact`} content="Contact" />
                                <Dropdown.Item as="a" href={`${trustUrl}/dashboard`} content="Dashboard" />
                                <Dropdown.Item as="a" href={`${trustUrl}/originator-search`} content="Originator" />
                                <Dropdown.Item as="a" href={`${trustUrl}/policy`} content="Policy" />
                                <Dropdown.Item as="a" href={`${trustUrl}/portfolio`} content="Portfolio" />
                                <Dropdown.Item as="a" href={`${trustUrl}/trust`} content="Trust" />
                            </>
                        )}

                        {userHasRole(Role.TrustCHBUser) && (
                            <>
                                <Dropdown.Item as="a" href={`${trustUrl}/closely-held-businesses`} content="Closely Held Business" />
                            </>
                        )}

                        {userHasRole(Role.Underwriting) && (
                            <>
                                <div className="navbar-dropdown-divider" />
                                <div className="navbar-dropdown-group-title">Underwriting</div>
                                <Dropdown.Item as="a" href={`${uwUrl}/ClientSearch`} content="Client" />
                                <Dropdown.Item as="a" href={`${uwUrl}/InsuredSearch`} content="Insured" />
                                <Dropdown.Item as="a" href={`${uwUrl}/RequestSearch`} content="Request" />
                            </>
                        )}
                    </Dropdown.Menu>
                </Dropdown>

                <Dropdown item text="Workflows">
                    <Dropdown.Menu className="navbar-menu-item">
                        {userHasRole(Role.ServicingUser) && (
                            <>
                                <div className="navbar-dropdown-group-title">Life Tracking</div>
                                <Dropdown.Item as={Link} to="/tracking-queue" content="Call Queue" />
                                <div className="navbar-dropdown-divider" />
                                <div className="navbar-dropdown-group-title">Medical Records</div>
                                <Dropdown.Item as={Link} to="/fax-inbox" content="Fax Inbox" />
                                <Dropdown.Item as={Link} to="/medrec-queue" content="Medical Records Dashboard" />
                                <Dropdown.Item as={Link} to="/refresh-queue" content="Meds LE Refresh Queue" />
                                <div className="navbar-dropdown-divider" />
                                <div className="navbar-dropdown-group-title">Servicing</div>
                                <Dropdown.Item as={Link} to="/carrier-requests" content="Carrier Requests" />
                                <Dropdown.Item as={Link} to="/client-policy-upload-queue" content="Client Policy Upload Queue" />
                                <Dropdown.Item as={Link} to="/death-claims" content="Death Claim Queue" />
                                <Dropdown.Item as={Link} to="/external-le-queue" content="External LE Queue" />
                                <Dropdown.Item as={Link} to="/grace-queue" content="Grace Queue" />
                                <Dropdown.Item as={Link} to="/payment-holds" content="Payment Holds" />
                                <Dropdown.Item as={Link} to="/policy-opt-queue" content="Policy Optimization Queue" />
                                <Dropdown.Item as={Link} to="/mass-upload" content="Premium Mass Upload" />
                                <Dropdown.Item as={Link} to="/policy-queue" content="Policy Queue" />
                                <Dropdown.Item as={Link} to="/term-whole-life" content="Term and Whole Life Policy Queue" />
                                {userHasRole(Role.ServicingTrading) && (
                                    <>
                                        <div className="navbar-dropdown-divider" />
                                        <div className="navbar-dropdown-group-title">Trading</div>
                                        <Dropdown.Item as={Link} to="/trading-queue" content="Trading Queue" />
                                    </>
                                )}
                            </>
                        )}

                        {userHasRole(Role.TrustUser) && (
                            <>
                                <div className="navbar-dropdown-divider" />
                                <div className="navbar-dropdown-group-title">Trust</div>
                                <Dropdown.Item
                                    as="a"
                                    href={`${connectUrl}/TrustManagement/TrustAdministrator/AdminConsole.aspx?IsManaged=Managed`}
                                    content="Admin Console"
                                />
                                <Dropdown.Item as="a" href={`${trustUrl}/asset-allocation-queue`} content="Asset Allocation Queue" />
                                <Dropdown.Item
                                    as="a"
                                    href={`${trustUrl}/trust-account-report-queue`}
                                    content="Trust Account Report Queue"
                                />
                                <Dropdown.Item
                                    as="a"
                                    href={`${connectUrl}/TrustManagement/DataEntry/PolicyAssessmentQueue.aspx`}
                                    content="Policy Assessment Queue"
                                />
                                <Dropdown.Item as="a" href={`${trustUrl}/carrier-request-queue`} content="Carrier Request Queue" />
                                <Dropdown.Item as="a" href={`${trustUrl}/check-queue`} content="Check Queue" />
                                <Dropdown.Item as="a" href={`${trustUrl}/deposit-slip-queue`} content="Deposit Slip Queue" />
                                <Dropdown.Item as="a" href={`${trustUrl}/print-queue`} content="Print Queue" />
                                <Dropdown.Item as={Link} to="/entrust-queue" content="EnTrust Queue" />
                                <Dropdown.Item as="a" href={`${trustUrl}/remediation-queue`} content="Remediation Queue" />
                                <Dropdown.Item as="a" href={`${trustUrl}/trust-trigger-queue`} content="Trigger Queue - Trust" />
                                <Dropdown.Item as="a" href={`${connectUrl}/TrustManagement/VarianceQueue.aspx`} content="Variance Queue" />
                                <Dropdown.Item as="a" href={`${trustUrl}/file-upload-queue`} content="File Upload Queue" />
                                <Dropdown.Item
                                    as="a"
                                    href={`${connectUrl}/TrustManagement/DocumentApprovalQueue.aspx`}
                                    content="Document Approval Queue"
                                />
                                <Dropdown.Item as="a" href={`${trustUrl}/grace-queue`} content="Trust Grace Queue" />
                                <Dropdown.Item as="a" href={`${trustUrl}/death-claim-queue`} content="Trust Death Claim Queue" />
                            </>
                        )}

                        {userHasRole(Role.TrustCHBUser) && (
                            <>
                                <Dropdown.Item
                                    as="a"
                                    href={`${trustUrl}/closely-held-business-queue`}
                                    content="Closely Held Business Queue"
                                />
                            </>
                        )}

                        {userHasRole(Role.Underwriting) && (
                            <>
                                <div className="navbar-dropdown-divider" />
                                <div className="navbar-dropdown-group-title">Underwriting</div>
                                <Dropdown.Item as="a" href={`${uwUrl}/OnlineSubmissions`} content="Online Submissions" />
                                <Dropdown.Item as="a" href={`${uwUrl}/SubmitQueue`} content="Shared Transmittal Queue" />
                                <Dropdown.Item as="a" href={`${uwUrl}/UWQueue`} content="UW Queue" />
                                <Dropdown.Item as="a" href={`${uwUrl}/UWSManagementQueue`} content="UWS Management Queue" />
                                <Dropdown.Item as="a" href={`${uwUrl}/MyCaseload`} content="My Caseload" />
                                <Dropdown.Item as="a" href={`${uwUrl}/MedRecHandlingQueue`} content="Medical Record Handling Queue" />
                            </>
                        )}
                    </Dropdown.Menu>
                </Dropdown>

                <Dropdown item text="Tools">
                    <Dropdown.Menu className="navbar-menu-item">
                        <Dropdown.Item as={Link} to="/inbound" content="Inbound" />
                        <Dropdown.Item
                            as="a"
                            href={`${connectUrl}/TrustManagement/DataEntry/DocumentQ.aspx`}
                            content="Ephesoft Document Queue"
                        />
                        <Dropdown.Item
                            as="a"
                            href={`${connectUrl}/TrustManagement/LetterTemplatesDashboard.aspx`}
                            content="Letter Templates Dashboard"
                        />
                        {userHasRole(Role.ServicingPolicyManager) && (
                            <Dropdown.Item
                                as="a"
                                href={`${connectUrl}/Servicing/BulkDataManagementTool.aspx`}
                                content="Bulk Data Management"
                            />
                        )}
                        <Dropdown.Item as={Link} to="/decrypt" content="Decrypt Tool" />

                        {userHasRole(Role.ServicingUser) && (
                            <>
                                <div className="navbar-dropdown-divider" />
                                <div className="navbar-dropdown-group-title">Life Tracking</div>
                                <Dropdown.Item as="a" href={`${connectUrl}/Servicing/ESweep.aspx`} content="ESweep Tools" />
                                {userHasRole(Role.MedRecMgr) && (
                                    <>
                                        <div className="navbar-dropdown-divider" />
                                        <div className="navbar-dropdown-group-title">Medical Records</div>
                                        <Dropdown.Item
                                            as="a"
                                            href={`${connectUrl}/Servicing/DoctorMerge.aspx`}
                                            content="Doctor Database Management"
                                        />
                                        <Dropdown.Item as={Link} to="/special-auth-files" content="Special Auth File Management" />
                                    </>
                                )}
                                <div className="navbar-dropdown-divider" />
                                <div className="navbar-dropdown-group-title">Servicing</div>
                                {userHasRole(Role.ServicingPolicyManager) && (
                                    <>
                                        <Dropdown.Item
                                            as="a"
                                            href={`${connectUrl}/Servicing/BulkPolicyUpdateTool.aspx`}
                                            content="Bulk Policy Update Tool"
                                        />
                                        <Dropdown.Item as={Link} to="/insureds-near-maturity" content="Insureds Near Maturity" />
                                    </>
                                )}
                                <Dropdown.Item as={Link} to="/validation-queue" content="Data Validation Queue" />
                                <Dropdown.Item as={Link} to="/document-management" content="Document Management" />
                                <Dropdown.Item as={Link} to="/mail-merge" content="Mail Merge" />
                                {userHasRole(Role.ServicingAdmin) && <Dropdown.Item as={Link} to="/policy-copy" content="Policy Copy" />}
                                <Dropdown.Item
                                    as="a"
                                    href={`${connectUrl}/Servicing/PolicyValuation.aspx`}
                                    content="Policy Valuation Tool"
                                />
                                {userHasRole(Role.ServicingTrading) && (
                                    <Dropdown.Item as={Link} to="/policy-valuation" content="Policy Valuation Tool (New)" />
                                )}
                                <Dropdown.Item as={Link} to="/portfolio-upload" content="Portfolio Upload Tool" />
                                <Dropdown.Item
                                    as="a"
                                    href={`${connectUrl}/Servicing/PremiumConfirmQueue.aspx`}
                                    content="Premium Confirm Queue"
                                />
                                <Dropdown.Item as={Link} to="/premium-confirm-queue" content="Premium Confirm Queue (New)" />
                                <Dropdown.Item as={Link} to="/product-library" content="Product Library" />
                                <Dropdown.Item as={Link} to="/secure-file-transfer" content="Secure File Transfer" />
                                <Dropdown.Item as={Link} to="/special-project-opt" content="Special Project Optimization" />
                                <div className="navbar-dropdown-divider" />
                            </>
                        )}

                        {userHasRole(Role.TrustUser) && (
                            <>
                                <div className="navbar-dropdown-divider" />
                                <div className="navbar-dropdown-group-title">Trust</div>
                                <Dropdown.Item as="a" href={`${trustUrl}/assessment-report`} content="Assessment Report" />
                                <Dropdown.Item
                                    as="a"
                                    href={`${connectUrl}/TrustManagement/InsightReporting/CompetitiveAnalysis.aspx`}
                                    content="Competitive Analysis"
                                />
                                <Dropdown.Item as="a" href={`${trustUrl}/document-management`} content="Document Management" />
                                <Dropdown.Item
                                    as="a"
                                    href={`${connectUrl}/TrustManagement/TrustUploadTool.aspx`}
                                    content="Trust Onboarding Tool"
                                />
                            </>
                        )}

                        {userHasRole(Role.LITCOAccounting) && (
                            <>
                                <Dropdown.Item as="a" href={`${trustUrl}/litco-fee-adjustment`} content="LITCO Fee Adjustment Tool" />
                            </>
                        )}

                        {userHasRole(Role.TrustManager) && (
                            <>
                                <Dropdown.Item as="a" href={`${trustUrl}/trust-onboard-tool`} content="LITCO Onboarding Tool" />
                                <Dropdown.Item as="a" href={`${trustUrl}/will-death-file-upload`} content="Will Death Date Import/Export" />
                            </>
                        )}

                        {userHasAnyRole([Role.UWAdmin, Role.UWSupportSr]) && (
                            <>
                                <div className="navbar-dropdown-divider" />
                                <div className="navbar-dropdown-group-title">Underwriting</div>
                                <Dropdown.Item as="a" href={`${uwUrl}/MessageInbox`} content="Message Inbox" />
                            </>
                        )}
                    </Dropdown.Menu>
                </Dropdown>

                <Dropdown item text="Reports">
                    <Dropdown.Menu className="navbar-menu-item">
                        {userHasRole(Role.ServicingUser) && (
                            <>
                                <div className="navbar-dropdown-group-title">Servicing</div>
                                <Dropdown.Item as={Link} to="/report-generator" content="Report Generator" />
                                <Dropdown.Item as={Link} to="/wire-instructions" content="Wire Instruction Report" />
                            </>
                        )}
                        {userHasRole(Role.TrustUser) && (
                            <>
                                <div className="navbar-dropdown-divider" />
                                <div className="navbar-dropdown-group-title">Trust</div>
                                <Dropdown.Item as="a" href={`${trustUrl}/report-dashboard`} content="Trust Report Dashboard" />
                            </>
                        )}
                        {userHasRole(Role.Underwriting) && (
                            <>
                                <div className="navbar-dropdown-divider" />
                                <div className="navbar-dropdown-group-title">Underwriting</div>
                                <Dropdown.Item as="a" href={`${uwUrl}/AnalyticsDashboard`} content="Analytics Dashboard" />
                                <Dropdown.Item as="a" href={`${uwUrl}/LRMReport`} content="LRM Data Enriched LE" />
                                <Dropdown.Item as="a" href={`${uwUrl}/SharedDashboard`} content="Shared Underwriting Dashboard" />
                                <Dropdown.Item
                                    as="a"
                                    href={`${uwUrl}/ReportGenerator/LEReceiptReport`}
                                    content="Underwriting Report Generator"
                                />
                            </>
                        )}
                    </Dropdown.Menu>
                </Dropdown>

                <Dropdown item text="Admin">
                    <Dropdown.Menu className="navbar-menu-item">
                        {userHasRole(Role.ServicingUser) && (
                            <>
                                <div className="navbar-dropdown-group-title">Management</div>
                                <Dropdown.Item
                                    as="a"
                                    href={`${connectUrl}/meddiag/reports/ReportTemplateEditor.aspx`}
                                    content="Report Template Editor"
                                />
                                <Dropdown.Item as={Link} to="/wire-template-editor" content="Report Template Editor (New)" />
                            </>
                        )}

                        {userHasAnyRole([Role.AccountingAdmin, Role.Accounting]) && (
                            <>
                                <div className="navbar-dropdown-divider" />
                                <div className="navbar-dropdown-group-title">Accounting</div>
                                <Dropdown.Item as={Link} to="/billing-export" content="Billing Export" />
                                <Dropdown.Item as="a" href={`${trustUrl}/billing-log`} content="Closely Held Business Billing" />
                                <Dropdown.Item
                                    as="a"
                                    href={`${trustUrl}/chb-billing-export`}
                                    content="Closely Held Business Billing Export"
                                />
                            </>
                        )}

                        <div className="navbar-dropdown-divider" />
                        <div className="navbar-dropdown-group-title">Maintenance</div>
                        <Dropdown.Item as="a" href={`${connectUrl}/admin/IpRestrictions.aspx`} content="IP Restrictions" />
                        <Dropdown.Item as="a" href={`${uwUrl}/MergeClients`} content="Merge Clients" />
                        <Dropdown.Item as="a" href={`${uwUrl}/MergeInsured`} content="Merge Insured" />
                        <Dropdown.Item as="a" href={`${uwUrl}/DataCleanupReport`} content="Data Cleanup" />
                        <Dropdown.Item as="a" href={`${connectUrl}/Servicing/FileMigration.aspx`} content="File Migration" />
                        {userHasAnyRole([Role.GenericAdmin, Role.UWSupportSr]) && (
                            <>
                                <Dropdown.Item as={Link} to="/create-client" content="Register New Client" />
                                <Dropdown.Item as="a" href={`${connectUrl}/admin/mergeinsured/rollback.aspx`} content="Rollback Merge" />
                            </>
                        )}
                        {userHasRole(Role.GenericAdmin) && (
                            <>
                                <Dropdown.Item as="a" href={`${connectUrl}/admin/RoleAdmin.aspx`} content="Role Admin" />
                                <Dropdown.Item as="a" href={`${connectUrl}/admin/userroles.aspx`} content="Employee User Admin" />
                                <Dropdown.Item as={Link} to="/user-admin" content="User Admin / Role Admin (New)" />
                            </>
                        )}
                        {userHasRole(Role.TrustManager) && (
                            <>
                                <Dropdown.Item as="a" href={`${trustUrl}/user`} content="IIQ Client User Admin" />
                                <Dropdown.Item
                                    as="a"
                                    href={`${trustUrl}/payment-instruction-admin`}
                                    content="Trust Premium Payment Instruction"
                                />
                            </>
                        )}
                        {userHasAnyRole([Role.UWAdmin, Role.UWSupportSr]) && (
                            <>
                                <Dropdown.Item as="a" href={`${uwUrl}/user`} content="21st LE Client User Admin" />
                            </>
                        )}
                        {userHasRole(Role.ServicingAdmin) && (
                            <>
                                <Dropdown.Item as={Link} to="/lexserv-user" content="LexServ Client User Admin" />
                                <Dropdown.Item as={Link} to="/longevity-analytics-user" content="Longevity Analytics Client User Admin" />
                                <Dropdown.Item as={Link} to="/longevity-analytics" content="Longevity Analytics Data Management" />
                            </>
                        )}
                    </Dropdown.Menu>
                </Dropdown>
            </Menu.Menu>
            <Menu.Menu position="right">
                <div className="mt-2">
                    <QuickSearch />
                </div>
                <Dropdown item text={user?.email}>
                    <Dropdown.Menu className="navbar-menu-item">
                        <Dropdown.Item onClick={() => logout()}>
                            <Icon name="log out" />
                            Log Out
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </Menu.Menu>
        </Menu>
    );
};

export default Navbar;
